import { isDefined, isEmpty } from "@clipboard-health/util-ts";
import { Alert, Autocomplete, Box, CircularProgress, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { TuFunction } from "@src/appV2/i18n/types";
import { useTu } from "@src/appV2/i18n/useTu";
import { useDebounce } from "@src/appV2/lib/utils";
import { formatCbhApiError, isCbhApiAxiosError } from "@src/appV2/lib/utils/errorUtils";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { CopyWorkplaceDialog } from "@src/containers/accountManagement/CopyFromWorkplace/CopyWorkplaceDialog";
import { type UseModalState, useModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { useGetFacilityProfile } from "../../../FacilityProfile/api/useGetFacilityProfile";
import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";
import { BerlinThemeProvider } from "../../theme/ThemeProvider";
import {
  type ChangeOfOwnershipOriginWorkplace,
  type ChangeOfOwnershipReplacementWorkplace,
  type ChangeOfOwnershipUpdate,
  type CurrentChangeOfOwnership,
} from "../types";
import { RemoveChangeOfOwnershipConfirmationDialog } from "./RemoveChangeOfOwnershipConfirmationDialog";
import { fromWorkplaceDayPickerValue, toWorkplaceDayPickerValue } from "../utils/workplaceDay";

// Resolving the preselected replacement workplace only needs its name and id. Space-separated:
// the facility-profile API splits projections on spaces, so a comma-joined value 400s.
const I18N_SKIP_NAME_PROJECTION = "name userId";

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  if (isCbhApiAxiosError(error)) {
    const firstError = error.response.data.errors[0];
    if (isDefined(firstError)) {
      const formatted = formatCbhApiError(firstError);
      if (formatted.length > 0) {
        return formatted;
      }
    }
  }
  return fallbackMessage;
}

function getNoOptionsText(searchInput: string, isLoading: boolean, tu: TuFunction): string {
  if (isEmpty(searchInput)) {
    return tu("Start typing to search for workplaces");
  }
  return isLoading ? tu("Searching workplaces...") : tu("No workplaces found");
}

/** Parses a stored ISO effective date into a Date, ignoring missing or invalid values. */
function parseEffectiveDate(effectiveDate: string | undefined): Date | undefined {
  if (!isDefined(effectiveDate)) {
    return undefined;
  }
  const parsed = new Date(effectiveDate);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export interface ChangeOfOwnershipDialogProps {
  modalState: UseModalState;
  origin: ChangeOfOwnershipOriginWorkplace;
  /** Existing change-of-ownership values; when present the dialog prefills for an update. */
  current?: CurrentChangeOfOwnership;
  /**
   * Persists the change of ownership; `null` clears it. Rejects on failure so the dialog can
   * surface the error.
   */
  onSave: (changeOfOwnership: ChangeOfOwnershipUpdate) => Promise<void>;
}

/**
 * Modal for recording or updating a facility's change of ownership. The admin selects the
 * replacement workplace (or creates a new one via the Copy Workplace flow) and an effective
 * date anchored to the facility's timezone, then saves. When updating, the admin can also
 * remove the recorded change of ownership entirely (behind a confirmation step).
 */
export function ChangeOfOwnershipDialog(props: ChangeOfOwnershipDialogProps) {
  const { modalState, origin, current, onSave } = props;
  const { tu } = useTu("changeOfOwnership");

  const copyModalState = useModalState();
  const confirmRemoveModalState = useModalState();

  const [replacement, setReplacement] = useState<ChangeOfOwnershipReplacementWorkplace | null>(
    null
  );
  // Stored as the UTC instant of midnight on the picked day in the facility's tz.
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeErrorMessage, setRemoveErrorMessage] = useState<string | undefined>(undefined);
  const [didPrefill, setDidPrefill] = useState(false);

  const debouncedSearch = useDebounce({ debounceTimeInMs: 300, value: searchInput });

  // Resolve the current replacement workplace's name so the selector shows it on an update.
  const currentReplacementId = current?.replacementFacilityId;
  const { data: currentReplacementProfile } = useGetFacilityProfile(
    currentReplacementId ?? "",
    I18N_SKIP_NAME_PROJECTION,
    { enabled: isDefined(currentReplacementId) && currentReplacementId.length > 0 }
  );

  // Prefill the form once from the persisted values. Later edits (including creating a new
  // facility) are never clobbered.
  useEffect(() => {
    if (didPrefill || isDefined(replacement) || !isDefined(currentReplacementProfile)) {
      return;
    }
    setReplacement({
      id: currentReplacementProfile.userId,
      name: currentReplacementProfile.name,
    });
    setEffectiveDate(parseEffectiveDate(current?.effectiveDate) ?? null);
    setDidPrefill(true);
  }, [didPrefill, replacement, currentReplacementProfile, current?.effectiveDate]);

  const { data: facilitiesData, isFetching: facilitiesLoading } = useSearchFacilities(
    { searchInput: debouncedSearch },
    { enabled: debouncedSearch.length > 0 }
  );

  const replacementOptions: ChangeOfOwnershipReplacementWorkplace[] =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .filter((facility: Facility) => facility.userId !== origin.id)
      .map((facility: Facility) => ({ id: facility.userId, name: facility.name })) ?? [];

  const resetAndClose = () => {
    setReplacement(null);
    setEffectiveDate(null);
    setSearchInput("");
    setErrorMessage(undefined);
    setIsSaving(false);
    setIsRemoving(false);
    setRemoveErrorMessage(undefined);
    setDidPrefill(false);
    confirmRemoveModalState.closeModal();
    modalState.closeModal();
  };

  const handleSave = async () => {
    if (!isDefined(replacement) || !isDefined(effectiveDate)) {
      return;
    }
    setErrorMessage(undefined);
    setIsSaving(true);
    try {
      await onSave({
        replacementFacilityId: replacement.id,
        effectiveDate: effectiveDate.toISOString(),
      });
      resetAndClose();
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          tu("Failed to save the change of ownership. Please try again or contact support.")
        )
      );
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoveErrorMessage(undefined);
    setIsRemoving(true);
    try {
      await onSave(null);
      resetAndClose();
    } catch (error) {
      setRemoveErrorMessage(
        getErrorMessage(
          error,
          tu("Failed to remove the change of ownership. Please try again or contact support.")
        )
      );
      setIsRemoving(false);
    }
  };

  const canSave = isDefined(replacement) && isDefined(effectiveDate);

  // Custom footer instead of DialogFooter: the update variant carries a third, destructive
  // action (Remove) in the bottom row next to Cancel, which DialogFooter doesn't support.
  // Layout and paddings mirror DialogFooter's horizontal layout.
  const footer = (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(3),
      })}
    >
      <Stack direction="row" spacing={3}>
        <Button disabled={isSaving} variant="outlined" onClick={resetAndClose}>
          {tu("Cancel")}
        </Button>
        {isDefined(current) && (
          <Button
            disabled={isSaving}
            variant="danger"
            onClick={() => {
              setRemoveErrorMessage(undefined);
              confirmRemoveModalState.openModal();
            }}
          >
            {tu("Remove change of ownership")}
          </Button>
        )}
      </Stack>
      <LoadingButton
        disabled={!canSave}
        isLoading={isSaving}
        variant="primary"
        onClick={() => {
          void handleSave();
        }}
      >
        {tu("Save")}
      </LoadingButton>
    </Box>
  );

  return (
    <>
      <BerlinThemeProvider>
        <ResponsiveDialog
          showCloseButton
          footerContent={footer}
          modalState={modalState}
          title={current ? tu("Update change of ownership") : tu("Record change of ownership")}
          width={520}
          onClose={resetAndClose}
        >
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Text semibold variant="body2">
                {tu("Workplace")}
              </Text>
              <Text variant="body2">{origin.name}</Text>
              <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
                {origin.address ?? tu("Address unavailable")}
              </Text>
            </Stack>

            <Stack spacing={1}>
              <Text semibold variant="body2">
                {tu("New owner workplace")}
              </Text>
              <Autocomplete
                autoComplete
                disabled={isSaving}
                filterOptions={(options) => options}
                getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === "string" || typeof value === "string") {
                    return false;
                  }
                  return option.id === value.id;
                }}
                loading={facilitiesLoading}
                loadingText={tu("Searching workplaces...")}
                noOptionsText={getNoOptionsText(searchInput, facilitiesLoading, tu)}
                options={replacementOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={tu("Start typing workplace name...")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {facilitiesLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(renderProps, option) => {
                  if (!option) {
                    return null;
                  }
                  return (
                    <li {...renderProps} key={option.id}>
                      <Stack direction="column">
                        <Text variant="body2">{option.name}</Text>
                        <Text
                          sx={(theme) => ({ color: theme.palette.text.secondary })}
                          variant="caption"
                        >
                          {tu("ID: {{id}}", { values: { id: option.id } })}
                        </Text>
                      </Stack>
                    </li>
                  );
                }}
                value={replacement}
                onChange={(_, newValue) => {
                  if (typeof newValue !== "string") {
                    setReplacement(newValue);
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setSearchInput(newInputValue);
                }}
              />
              <Button
                disabled={isSaving}
                size="small"
                startIconType="plus"
                sx={{ alignSelf: "flex-start" }}
                variant="link"
                onClick={() => {
                  copyModalState.openModal();
                }}
              >
                {tu("New Facility")}
              </Button>
            </Stack>

            <Stack spacing={1}>
              <Text semibold variant="body2">
                {tu("Effective date")}
              </Text>
              <DatePicker
                disabled={isSaving}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    inputProps: { "aria-label": tu("Effective date") },
                  },
                }}
                value={toWorkplaceDayPickerValue(effectiveDate, origin.tmz)}
                onChange={(date) => {
                  setEffectiveDate(fromWorkplaceDayPickerValue(date, origin.tmz));
                }}
              />
            </Stack>

            {isDefined(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </ResponsiveDialog>

        {confirmRemoveModalState.modalIsOpen && (
          <RemoveChangeOfOwnershipConfirmationDialog
            errorMessage={removeErrorMessage}
            isRemoving={isRemoving}
            modalState={confirmRemoveModalState}
            originName={origin.name}
            onConfirm={() => {
              void handleRemove();
            }}
          />
        )}
      </BerlinThemeProvider>

      {/* Rendered outside BerlinThemeProvider so the legacy Copy Workplace dialog keeps the
          app's MUI theme and we avoid style drift. */}
      <CopyWorkplaceDialog
        modalState={copyModalState}
        presetSourceWorkplace={{ userId: origin.id, name: origin.name }}
        lockSourceAndChow
        onCreated={(workplace) => {
          setReplacement({ id: workplace.userId, name: workplace.name });
          setDidPrefill(true);
          copyModalState.closeModal();
        }}
      />
    </>
  );
}
